import moment from 'moment';
import React from 'react';
import { useRecoilValue } from 'recoil';
import { AAdditionalColumns } from '../../atoms/additionalColumns';
import { AFormFields, Field } from '../../atoms/forms';
import { Product } from '../../atoms/product';
import Dropdown from '../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../components_v2/dropdown/model/Model';
import { DefaultButton } from '../../styles/global/css/GlobalButton';
import { PrimaryColor, BorderColor, BorderColor2, GreySidely3, RedSidely } from '../../styles/global/css/Utils';
import { Translate, translateToString } from '../../styles/global/translate';
import { importWrapper } from '../../utils/import';
import { AlertCall, AlertRes } from '../alert/AlertProvider';
import { Settings } from 'lucide-react';
import styled from 'styled-components';
import { DeleteDot } from '../../styles/global/css/Dot';
import { creationPopupDropdownStyle } from '../../components_v2/popup/style/CreationFormStyle';
import { launchToastError } from '../forms/visual-form-editor/visualFormEditor';
import { FlexDiv } from '../products/style';
import { importPromotionsCall } from './actions';

type AdditionalColumnTypeLst = 'id' | 'is_period' | 'name' | 'period_name' | 'is_active' | 'description' | 'color' | 'start_date' | 'end_date' | 'product_id' | 'product_barcode';
type TemplateImport = Record<string, unknown>;
type Fields = { [key: number]: unknown };
type HeaderValue = AdditionalColumnTypeLst | { field: number, period?: string } | { period: string, type: AdditionalColumnTypeLst };
type Campaign = { id?: number, name: string, is_active: boolean, description?: string, fields?: Fields };
type Period = { name: string, color: string, start: moment.Moment, end: moment.Moment, fields?: Fields };
type PeriodCampaignAndProduct = (Campaign | Period) & { product_id?: number };
// period_name holds the file's own name for a period: `name` stays the mapping
// group ("Période 1") so every column of the group still merges into one row
type Row = Partial<Campaign> & Partial<Period> & { is_period?: boolean, product_id?: number, period_name?: string };
type ImportCampaignAndPeriodsMaps = Campaign & { periods: Map<string, Period>, products: Set<number> };
export type ImportCampaignAndPeriods = Campaign & { periods: Period[], products?: number[] };
export type PivotColumn = 'name' | { field: number }

// Header matching for the mapping auto-fill: accents, case and punctuation
// insensitive; exact matches win over containment, and short tokens (id…)
// only match exactly
export const normalizeHeader = (s: unknown): string => String(s ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');

export function bestMatch(normHeaders: string[], candidates: string[], used: Set<number>): number | undefined {
	const cands = candidates.map(normalizeHeader).filter(c => c.length > 0);
	for (const c of cands) {
		const i = normHeaders.findIndex((h, idx) => !used.has(idx) && h === c);
		if (i >= 0) return i;
	}
	for (const c of cands.filter(c => c.length >= 3)) {
		const i = normHeaders.findIndex((h, idx) => !used.has(idx) && h.length >= 3 && (h.includes(c) || c.includes(h)));
		if (i >= 0) return i;
	}
	return undefined;
}

// Translated import errors: the line number as shown in the file (headers
// are line 1) and the file's real column names, in the user's language
type ImportErrors = ReturnType<typeof importErrors>;
function importErrors(rawHeaders: unknown[]) {
	let line = 0;
	const columnName = (index: number) => String(rawHeaders[index] ?? '').trim() || `${translateToString('import.promotions.file_column')} ${index + 1}`;
	const err = (key: string, variables: [string, string][]): never => {
		throw translateToString(`import.promotions.errors.${key}`, [['line', String(line)], ...variables]);
	};
	return {
		setLine: (rowIndex: number) => line = rowIndex + 1,
		wrongType: (headerIndex: number, expected: 'number' | 'text' | 'bool' | 'date', value: unknown): never =>
			err('wrong_type', [['value', String(value)], ['column', columnName(headerIndex)], ['expected', translateToString(`import.promotions.errors.expected_${expected}`)]]),
		unknownProductId: (value: unknown): never => err('unknown_product_id', [['value', String(value)]]),
		invalidDate: (headerIndex: number, value: unknown): never => err('invalid_date', [['column', columnName(headerIndex)], ['value', String(value)]]),
		endBeforeStart: (): never => err('end_before_start', []),
		missing: (name: string): never => err('missing_value', [['name', name]]),
		generic: (): never => err('generic', []),
	};
}

// A row whose EAN matches no product is imported without the product link
// instead of failing the whole file; the skips are reported at the end and
// downloadable as CSV
export type SkippedProduct = { line: number, value: string, name?: string };

function mandatory<T>(value: T | undefined, name: string, errors: ImportErrors): T {
	if (value == undefined) errors.missing(name);
	return value!;
}

function rowToCampaignOrPeriod(row: Row, errors: ImportErrors): PeriodCampaignAndProduct {
	if (row.is_period) {
		return {
			name: row.period_name?.trim() || mandatory(row.name, translateToString('name'), errors),
			color: row.color ?? PrimaryColor,
			start: mandatory(row.start, `${translateToString('import.sidely_fields.start_date')} (${row.name ?? ''})`, errors),
			end: mandatory(row.end, `${translateToString('import.sidely_fields.end_date')} (${row.name ?? ''})`, errors),
			fields: row.fields,
			product_id: row.product_id,
		};
	}
	return {
		id: row.id,
		name: mandatory(row.name, translateToString('name'), errors),
		is_active: row.is_active ?? true,
		description: row.description,
		fields: row.fields,
		product_id: row.product_id,
	};
}


function parseRows(headers: Map<number, HeaderValue>, rows: unknown[], productBarcodeMap: Map<string, number>, productIdSet: Set<number>, skipped: SkippedProduct[]): PeriodCampaignAndProduct[] {
	const errors = importErrors(Array.isArray(rows[0]) ? rows[0] : []);
	let rowSkips: string[] = [];
	function switchHeader(res: Row, value: unknown, header: HeaderValue, headerIndex: number, pushRes = false): Row[] {
		switch (header) {
			case 'product_id':
				if (typeof value === 'number') {
					if (!productIdSet.has(value)) {
						errors.unknownProductId(value);
					}
					res.product_id = value;
				} else {
					errors.wrongType(headerIndex, 'number', value);
				}
				break;
			case 'product_barcode':
				if (typeof value === 'string' || typeof value === 'number') {
					const s = value.toString();
					if (!productBarcodeMap.has(s)) {
						// unknown EAN: the row imports without its product link
						rowSkips.push(s);
						break;
					}
					res.product_id = productBarcodeMap.get(s);
				} else {
					errors.wrongType(headerIndex, 'text', value);
				}
				break;
			case 'id':
				if (typeof value === 'number') {
					res.id = value;
				} else {
					errors.wrongType(headerIndex, 'number', value);
				}
				break;
			case 'is_period':
				if (typeof value === 'boolean') {
					res.is_period = value;
				} else {
					errors.wrongType(headerIndex, 'bool', value);
				}
				break;
			case 'name':
				if (typeof value === 'string') {
					res.name = value;
				} else {
					errors.wrongType(headerIndex, 'text', value);
				}
				break;
			case 'period_name':
				// numbers survive the json parse of the cell, and a period named
				// "2024" is ordinary
				if (typeof value === 'string' || typeof value === 'number') {
					res.period_name = value.toString();
				} else {
					errors.wrongType(headerIndex, 'text', value);
				}
				break;
			case 'is_active':
				if (typeof value === 'boolean') {
					res.is_active = value;
				} else {
					errors.wrongType(headerIndex, 'bool', value);
				}
				break;
			case 'description':
				if (typeof value === 'string') {
					res.description = value;
				} else {
					errors.wrongType(headerIndex, 'text', value);
				}
				break;
			case 'color':
				if (typeof value === 'string') {
					res.color = value;
				} else {
					errors.wrongType(headerIndex, 'text', value);
				}
				break;
			case 'start_date':
				if (value instanceof Date || typeof value == 'number' || typeof value === 'string') {
					res.start = moment(value);
				} else {
					errors.wrongType(headerIndex, 'date', value);
					break;
				}
				if (res.start?.isValid() === false) {
					errors.invalidDate(headerIndex, value);
				}
				if (res.start && res.end && res.start.isAfter(res.end)) {
					errors.endBeforeStart();
				}
				break;
			case 'end_date':
				if (value instanceof Date || typeof value == 'number' || typeof value === 'string') {
					res.end = moment(value);
				} else {
					errors.wrongType(headerIndex, 'date', value);
					break;
				}
				if (res.end?.isValid() === false) {
					errors.invalidDate(headerIndex, value);
				}
				if (res.start && res.end && res.start.isAfter(res.end)) {
					errors.endBeforeStart();
				}
				break;
			default:
				if ('field' in header) {
					if (header.period !== undefined) {
						if (pushRes) {
							return [res, ...switchHeader({ name: header.period, is_period: true }, value, { field: header.field }, headerIndex, true)];
						} else {
							return switchHeader({ name: header.period, is_period: true }, value, { field: header.field }, headerIndex, true);
						}
					}
					if (res.fields == undefined) res.fields = {};
					res.fields[header.field] = value;
				} else {
					if (pushRes) {
						return [res, ...switchHeader({ name: header.period, is_period: true }, value, header.type, headerIndex, true)];
					} else {
						return switchHeader({ name: header.period, is_period: true }, value, header.type, headerIndex, true);
					}
				}
				break;
		}
		if (pushRes) return [res];
		return [];

	}

	return rows.reduce<PeriodCampaignAndProduct[]>((acc, row, rowIndex) => {
		errors.setLine(rowIndex);
		rowSkips = [];
		try {
			if (rowIndex === 0) return acc;
			if (!Array.isArray(row)) {
				// generic() throws; the return only narrows `row` for the compiler
				errors.generic();
				return acc;
			}
			if (row.length == 0 || (row.length == 1 && !row[0])) return acc;
			const res: Row = {};
			const additionalRows: Row[] = [];
			for (const [headerIndex, header] of headers.entries()) {
				let value = row[headerIndex];
				if (value === undefined || value === '' || value === null) continue;
				try {
					value = JSON.parse(value as string);
				} catch (_e) { /* empty */ }
				additionalRows.push(...switchHeader(res, value, header, headerIndex));
			}
			acc.push(rowToCampaignOrPeriod(res, errors));
			acc.push(...mergeAdditionalRows(additionalRows, errors).map(r => rowToCampaignOrPeriod(r, errors)));
			rowSkips.forEach(value => skipped.push({ line: rowIndex + 1, value, name: res.name }));
		} catch (e) {
			// helper errors are already translated and carry the line
			if (typeof e === 'string') throw e;
			errors.generic();
		}
		return acc;
	}, []);
}

function mergeAdditionalRows(additionalRows: Row[], errors: ImportErrors): Row[] {
	const nameMap = new Map<string, Row>();
	additionalRows.forEach(row => {
		if (row.is_period) {
			const name = mandatory(row.name, translateToString('period'), errors);
			if (nameMap.has(name)) {
				const existing = nameMap.get(name)!;
				let newMap: Fields | undefined = undefined;
				if (existing.fields !== undefined || row.fields !== undefined) {
					newMap = { ...existing.fields, ...row.fields };
				}
				Object.assign(existing, row);
				existing.fields = newMap;
			} else {
				nameMap.set(name, row);
			}
		}
	});
	return [...nameMap.values()];
}

function transformData(headers: Map<number, HeaderValue>, data: unknown[], productBarcodeMap: Map<string, number>, productIdSet: Set<number>): { campaigns: ImportCampaignAndPeriods[], skipped: SkippedProduct[] } {
	const skipped: SkippedProduct[] = [];
	try {
		const rows = parseRows(headers, data, productBarcodeMap, productIdSet, skipped);
		return {
			campaigns: rows.reduce<ImportCampaignAndPeriodsMaps[]>((acc, row, index) => {
				if ('is_active' in row) {
					if (acc.length !== 0) {
						const lastElement = acc[acc.length - 1];
						const hasId = lastElement.id !== undefined || row.id !== undefined;
						if ((hasId && lastElement.id === row.id) || (!hasId && row.name !== undefined && lastElement.name === row.name)) {
							if (row.product_id) {
								lastElement.products.add(row.product_id!);
							}
						} else {
							acc.push({ ...row, periods: new Map(), products: row.product_id ? new Set([row.product_id!]) : new Set() });
						}
						return acc;
					}
					acc.push({ ...row, periods: new Map(), products: row.product_id ? new Set([row.product_id!]) : new Set() });
				} else {
					if (acc.length === 0) throw translateToString('import.promotions.errors.orphan_period', [['line', String(index + 1)]]);
					if (acc[acc.length - 1].periods.has(row.name)) return acc;
					acc[acc.length - 1].periods.set(row.name, row);
				}
				return acc;
			}, []).map((c) => ({ ...c, periods: [...c.periods.values()], products: [...c.products] })),
			skipped
		};
	} catch (e) {
		launchToastError(e);
		return { campaigns: [], skipped: [] };
	}
}

// the creation popups' field label: 12px, red star when required
const FieldTitle = styled.h5<{ required?: boolean }>`
	font-size: 12px;
	margin: 0;
	font-weight: 400;

	${p => p.required ? `
		&:before {
			content: '*';
			color: ${RedSidely};
			margin-right: 3px;
		}
	` : ''}
`;

const SectionTitle = styled.h5`
	font-size: 13px;
	margin: 0;
	font-weight: 500;
`;

const Separator = styled.div`
	width: 100%;
	height: 1px;
	background-color: ${BorderColor};
`;

type MappingResult = {
	headers: Map<number, HeaderValue>,
	pivotColumn: PivotColumn | undefined,
	companyFieldMappings: { additional_column_id: number, field_id: number }[],
};

type PeriodMapping = { name?: number, start?: number, end?: number, color?: number };

// the last confirmed mapping, keyed by normalized header text so it survives
// column reordering and new columns; restored ahead of the auto-fill
const MAPPING_STORAGE_KEY = 'promotions_import_mapping';
type SavedPeriod = { name?: string, start?: string, end?: string, color?: string };
type SavedMapping = {
	campaign?: Partial<Record<CampaignTarget, string>>,
	periods?: SavedPeriod[],
	fields?: Record<string, string>,
	links?: Record<string, number>,
	pivot?: PivotColumn,
};

function loadSavedMapping(): SavedMapping {
	try {
		return JSON.parse(localStorage.getItem(MAPPING_STORAGE_KEY) ?? '{}');
	} catch {
		return {};
	}
}

const CAMPAIGN_TARGETS = ['id', 'name', 'description', 'is_active', 'product_barcode', 'product_id'] as const;
type CampaignTarget = typeof CAMPAIGN_TARGETS[number];

const TARGET_LABELS: Record<CampaignTarget, () => string> = {
	id: () => 'Id',
	name: () => translateToString('name'),
	description: () => translateToString('description'),
	is_active: () => translateToString('active'),
	product_barcode: () => translateToString('import.sidely_fields.barcode'),
	product_id: () => `Id (${translateToString('product.product_name')})`,
};

// what each target answers to in a header row, on top of its display label
const TARGET_CANDIDATES: Record<CampaignTarget, string[]> = {
	id: ['id'],
	name: ['name', 'campagne', 'campaign', 'promotion'],
	description: ['description'],
	is_active: ['is_active', 'actif', 'active'],
	product_barcode: ['product_barcode', 'code barre', 'barcode', 'ean', 'gencod'],
	product_id: ['product_id'],
};

async function transformAndImportData(data: unknown[], productBarcodeMap: Map<string, number>, productIdSet: Set<number>, alert: AlertCall): Promise<(() => Promise<void>) | undefined> {
	if (!Array.isArray(data[0])) {
		launchToastError(translateToString('import.promotions.errors.bad_file'));
		return;
	}
	const rawHeaders: unknown[] = data[0];

	const alertRes = await alert<MappingResult>({
		title: <Translate id='general.import' />,
		buttonMargin: '0px',
		noButtons: true,
		overflow: 'unset',
		width: 'min(960px, 95vw)',
		content: (_, onOk) => {
			const additionalColumns = useRecoilValue(AAdditionalColumns);
			const fields = useRecoilValue(AFormFields);
			const promoFields = React.useMemo(() => fields.filter(f => f.is_promotion && !f.is_product), [fields]);
			const normHeaders = React.useMemo(() => rawHeaders.map(normalizeHeader), []);
			const columnOptions = React.useMemo<DropdownData<number>[]>(
				() => rawHeaders.map((h, i) => ({ label: String(h ?? '').trim() || `${translateToString('import.promotions.file_column')} ${i + 1}`, value: i })),
				[],
			);

			// the saved mapping restores first (exact header text), then the
			// auto-fill completes the rest; each column is claimed only once
			const init = React.useMemo(() => {
				const saved = loadSavedMapping();
				const used = new Set<number>();
				const resolve = (header?: string): number | undefined => {
					if (!header) return undefined;
					const i = normHeaders.findIndex((h, idx) => !used.has(idx) && h === header);
					if (i >= 0) used.add(i);
					return i >= 0 ? i : undefined;
				};
				const take = (candidates: string[]): number | undefined => {
					const i = bestMatch(normHeaders, candidates, used);
					if (i !== undefined) used.add(i);
					return i;
				};
				const campaign = CAMPAIGN_TARGETS.reduce<Partial<Record<CampaignTarget, number>>>((acc, target) => {
					acc[target] = resolve(saved.campaign?.[target]) ?? take([TARGET_LABELS[target](), ...TARGET_CANDIDATES[target]]);
					return acc;
				}, {});
				const startLabel = translateToString('import.sidely_fields.start_date');
				const endLabel = translateToString('import.sidely_fields.end_date');
				const colorLabel = translateToString('color');
				const periodLabel = translateToString('period');
				const nameLabel = translateToString('name');
				const periods: PeriodMapping[] = [];
				for (let i = 1; i <= 20; i++) {
					// both orders answer: "Date de début (Période 1)" and the
					// template's "Période 1 - Date de début", plus the legacy
					// start_date:period1 tokens and english spellings
					const savedPeriod = saved.periods?.[i - 1];
					const p: PeriodMapping = {
						name: resolve(savedPeriod?.name) ?? take([`name:period${i}`, `${periodLabel} ${i} ${nameLabel}`, `${nameLabel} (${periodLabel} ${i})`, `periode ${i} nom`, `period ${i} name`, `nom periode ${i}`]),
						start: resolve(savedPeriod?.start) ?? take([`start_date:period${i}`, `${startLabel} (${periodLabel} ${i})`, `${periodLabel} ${i} ${startLabel}`, `periode ${i} date de debut`, `period ${i} start date`, `debut periode ${i}`]),
						end: resolve(savedPeriod?.end) ?? take([`end_date:period${i}`, `${endLabel} (${periodLabel} ${i})`, `${periodLabel} ${i} ${endLabel}`, `periode ${i} date de fin`, `period ${i} end date`, `fin periode ${i}`]),
						color: resolve(savedPeriod?.color) ?? take([`color:period${i}`, `${colorLabel} (${periodLabel} ${i})`, `${periodLabel} ${i} ${colorLabel}`, `periode ${i} couleur`, `period ${i} color`]),
					};
					if (p.name === undefined && p.start === undefined && p.end === undefined && p.color === undefined) break;
					periods.push(p);
				}
				if (periods.length === 0) periods.push({});
				const fieldColumns = new Map<number, number>();
				for (const f of promoFields) {
					const i = resolve(saved.fields?.[String(f.id)]) ?? take([String(f.id), f.name]);
					if (i !== undefined) fieldColumns.set(f.id, i);
				}
				// links and pivot restore only while still valid: the additional
				// column must exist and the paired field must be mapped
				const links = new Map<number, number | undefined>();
				for (const [fieldId, acId] of Object.entries(saved.links ?? {})) {
					if (fieldColumns.get(Number(fieldId)) !== undefined && additionalColumns.some(ac => ac.id === acId)) {
						links.set(Number(fieldId), acId);
					}
				}
				let pivot = saved.pivot;
				if (pivot === 'name' && campaign.name === undefined) pivot = undefined;
				if (typeof pivot === 'object' && fieldColumns.get(pivot.field) === undefined) pivot = undefined;
				return { campaign, periods, fieldColumns, links, pivot };
			}, [promoFields]);

			const [campaign, setCampaign] = React.useState(init.campaign);
			const [periods, setPeriods] = React.useState<PeriodMapping[]>(init.periods);
			const [fieldColumns, setFieldColumns] = React.useState<Map<number, number | undefined>>(init.fieldColumns);
			// field id -> chosen additional column; key presence = checkbox on
			const [links, setLinks] = React.useState<Map<number, number | undefined>>(init.links);
			const [pivotColumn, setPivotColumn] = React.useState<PivotColumn | undefined>(init.pivot);

			// "ID externe": replaces the file id with a mapped field's value
			// (the backend pivot); ticking disables and ignores the Id column
			const [externalId, setExternalId] = React.useState(init.pivot !== undefined);
			// the field whose config zone (company linking) is open
			const [configFieldId, setConfigFieldId] = React.useState<number>();

			const tableGrid: React.CSSProperties = { display: 'grid', gridTemplateColumns: '190px minmax(0, 1fr) 30px', alignItems: 'center', rowGap: '10px', columnGap: '12px', width: '100%' };
			const spanRow: React.CSSProperties = { gridColumn: '1 / -1' };
			const headerCell: React.CSSProperties = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: GreySidely3, paddingBottom: '4px', borderBottom: `1px solid ${BorderColor}` };
			// the two columns the popup reads as a table: the promotion side and
			// the file side, repeated above each section so a scrolled grid keeps
			// saying which half is which
			const MappingHeader = () => <>
				<div style={headerCell}>{translateToString('import.promotions.promotion_field')}</div>
				<div style={headerCell}>{translateToString('import.promotions.file_column_header')}</div>
				<div style={headerCell} />
			</>;
			const hintStyle: React.CSSProperties = { margin: 0, fontSize: '12px', color: GreySidely3 };

			const ColumnSelect = (props: { value?: number, onChange: (v?: number) => void, disabled?: boolean }) => <Dropdown<number>
				cancellable
				disabled={props.disabled}
				dropdownStyle={creationPopupDropdownStyle}
				name=''
				datalist={columnOptions}
				selectedValue={props.value !== undefined ? columnOptions.find(o => o.value === props.value) : undefined}
				onChange={(v) => props.onChange(v.value ?? undefined)}
			/>;

			// one table row: the target's name, the file column select, and the
			// config wheel when the row has options (company linking)
			const MappingRow = (props: { label: string, required?: boolean, value?: number, onChange: (v?: number) => void, disabled?: boolean, gear?: () => void, gearActive?: boolean }) => <>
				<FieldTitle required={props.required} title={props.label} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '13px' }}>{props.label}</FieldTitle>
				<ColumnSelect value={props.value} onChange={props.onChange} disabled={props.disabled} />
				{props.gear
					? <Settings size={18} color={props.gearActive ? PrimaryColor : GreySidely3} style={{ cursor: 'pointer' }} onClick={props.gear} />
					: <div />}
			</>;

			const externalIdOptions = React.useMemo<DropdownData<PivotColumn>[]>(() => [
				...(campaign.name !== undefined ? [{ label: translateToString('name'), value: 'name' as PivotColumn }] : []),
				...promoFields.filter(f => fieldColumns.get(f.id) !== undefined).map(f => ({ label: f.name, value: { field: f.id } })),
			], [campaign.name, fieldColumns, promoFields]);

			const submit = () => {
				if (campaign.name === undefined) {
					launchToastError(translateToString('import.promotions.name_required'));
					return;
				}
				if (externalId && pivotColumn === undefined) {
					launchToastError(translateToString('import.promotions.external_id_pick'));
					return;
				}
				const activePeriods = periods.filter(p => p.name !== undefined || p.start !== undefined || p.end !== undefined || p.color !== undefined);
				if (activePeriods.some(p => p.start === undefined || p.end === undefined)) {
					launchToastError(translateToString('import.promotions.period_bounds'));
					return;
				}
				const headers = new Map<number, HeaderValue>();
				for (const target of CAMPAIGN_TARGETS) {
					// the external id replaces the file id entirely
					if (target === 'id' && externalId) continue;
					const index = campaign[target];
					if (index !== undefined) headers.set(index, target);
				}
				activePeriods.forEach((p, i) => {
					const period = `${translateToString('period')} ${i + 1}`;
					headers.set(p.start!, { period, type: 'start_date' });
					headers.set(p.end!, { period, type: 'end_date' });
					if (p.color !== undefined) headers.set(p.color, { period, type: 'color' });
					if (p.name !== undefined) headers.set(p.name, { period, type: 'period_name' });
				});
				for (const [fieldId, index] of fieldColumns.entries()) {
					if (index !== undefined) headers.set(index, { field: fieldId });
				}
				const companyFieldMappings = [...links.entries()]
					.filter(([fieldId, ac]) => ac !== undefined && fieldColumns.get(fieldId) !== undefined)
					.map(([field_id, additional_column_id]) => ({ field_id, additional_column_id: additional_column_id! }));
				const pivot = externalId ? pivotColumn : undefined;
				// remember the confirmed mapping (by header text) for next time
				const header = (i?: number) => i !== undefined ? normHeaders[i] : undefined;
				const savedMapping: SavedMapping = {
					campaign: CAMPAIGN_TARGETS.reduce<Partial<Record<CampaignTarget, string>>>((acc, target) => {
						acc[target] = header(campaign[target]);
						return acc;
					}, {}),
					periods: activePeriods.map(p => ({ name: header(p.name), start: header(p.start), end: header(p.end), color: header(p.color) })),
					fields: Object.fromEntries([...fieldColumns.entries()].filter(([, i]) => i !== undefined).map(([fieldId, i]) => [String(fieldId), header(i)!])),
					links: Object.fromEntries(companyFieldMappings.map(m => [String(m.field_id), m.additional_column_id])),
					pivot,
				};
				try {
					localStorage.setItem(MAPPING_STORAGE_KEY, JSON.stringify(savedMapping));
				} catch { /* empty */ }
				onOk({ headers, pivotColumn: pivot, companyFieldMappings });
			};

			// ticking the box prefills the additional column: the one linked to
			// the very field first, else the closest name
			const toggleLink = (field: Field) => setLinks(prev => {
				const next = new Map(prev);
				if (next.has(field.id)) {
					next.delete(field.id);
					return next;
				}
				const linked = additionalColumns.find(ac => ac.field_id === field.id);
				const byName = linked ?? additionalColumns.find(ac => normalizeHeader(ac.name) === normalizeHeader(field.name))
					?? additionalColumns.find(ac => {
						const a = normalizeHeader(ac.name);
						const b = normalizeHeader(field.name);
						return a.length >= 3 && b.length >= 3 && (a.includes(b) || b.includes(a));
					});
				next.set(field.id, byName?.id);
				return next;
			});

			const configField = configFieldId !== undefined && fieldColumns.get(configFieldId) !== undefined
				? promoFields.find(f => f.id === configFieldId)
				: undefined;

			return <FlexDiv align='flex-start' gap='20px' style={{ width: '100%', textAlign: 'initial', maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
				<FlexDiv flow='column' align='stretch' gap='12px' style={{ flex: 1, minWidth: 0 }}>
					<SectionTitle><Translate id='campaign' /></SectionTitle>
					<Separator />
					<p style={hintStyle}><Translate id='import.promotions.pivot_hint' /></p>
					<div style={tableGrid}>
						<MappingHeader />
						{CAMPAIGN_TARGETS.map(target => <React.Fragment key={target}>
							<MappingRow
								label={TARGET_LABELS[target]()}
								required={target === 'name'}
								value={campaign[target]}
								disabled={target === 'id' && externalId}
								onChange={(v) => {
									if (target === 'name' && v === undefined && pivotColumn === 'name') setPivotColumn(undefined);
									setCampaign(c => ({ ...c, [target]: v }));
								}}
							/>
							{target === 'id' && <>
								<label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer', margin: 0, fontSize: '12px', color: GreySidely3 }}>
									<input type='checkbox' checked={externalId} onChange={() => setExternalId(checked => {
										if (checked) setPivotColumn(undefined);
										return !checked;
									})} />
									<Translate id='import.promotions.external_id' />
								</label>
								{externalId ? <Dropdown<PivotColumn>
									cancellable
									dropdownStyle={creationPopupDropdownStyle}
									name=''
									placeholder={translateToString('import.promotions.external_id')}
									datalist={externalIdOptions}
									selectedValue={externalIdOptions.find(o => JSON.stringify(o.value) === JSON.stringify(pivotColumn))}
									onChange={(v) => setPivotColumn(v.value ?? undefined)}
								/> : <div />}
								<div />
							</>}
						</React.Fragment>)}
					</div>
					<SectionTitle style={{ marginTop: '10px' }}><Translate id='period' />s</SectionTitle>
					<Separator />
					<div style={tableGrid}>
						<MappingHeader />
						{periods.map((p, i) => <React.Fragment key={i}>
							<div style={{ ...spanRow, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
								<SectionTitle style={{ fontWeight: 400 }}>{`${translateToString('period')} ${i + 1}`}</SectionTitle>
								<DeleteDot size='18px' onClick={() => setPeriods(list => list.filter((_, j) => j !== i))} />
							</div>
							<MappingRow label={translateToString('period_name')} value={p.name} onChange={(v) => setPeriods(list => list.map((x, j) => j === i ? { ...x, name: v } : x))} />
							<MappingRow label={translateToString('import.sidely_fields.start_date')} required value={p.start} onChange={(v) => setPeriods(list => list.map((x, j) => j === i ? { ...x, start: v } : x))} />
							<MappingRow label={translateToString('import.sidely_fields.end_date')} required value={p.end} onChange={(v) => setPeriods(list => list.map((x, j) => j === i ? { ...x, end: v } : x))} />
							<MappingRow label={translateToString('color')} value={p.color} onChange={(v) => setPeriods(list => list.map((x, j) => j === i ? { ...x, color: v } : x))} />
						</React.Fragment>)}
						<span style={{ ...spanRow, color: PrimaryColor, cursor: 'pointer', fontSize: '13px' }} onClick={() => setPeriods(list => [...list, {}])}>
							+ <Translate id='import.promotions.add_period' />
						</span>
					</div>
					{promoFields.length > 0 && <>
						<SectionTitle style={{ marginTop: '10px' }}><Translate id='import.promotions.fields' /></SectionTitle>
						<Separator />
						<div style={tableGrid}>
							<MappingHeader />
							{promoFields.map(field => <MappingRow
								key={field.id}
								label={field.name}
								value={fieldColumns.get(field.id)}
								gear={fieldColumns.get(field.id) !== undefined ? () => setConfigFieldId(prev => prev === field.id ? undefined : field.id) : undefined}
								gearActive={configFieldId === field.id || links.has(field.id)}
								onChange={(v) => {
									if (v === undefined && typeof pivotColumn === 'object' && pivotColumn.field === field.id) setPivotColumn(undefined);
									setFieldColumns(prev => new Map(prev).set(field.id, v));
								}}
							/>)}
						</div>
					</>}
					<FlexDiv justify='center' style={{ width: '100%' }}>
						<DefaultButton margin='15px 0 0 0' onClick={submit}><Translate id='general.import' /></DefaultButton>
					</FlexDiv>
				</FlexDiv>
				{configField && <FlexDiv flow='column' align='stretch' gap='10px' style={{ width: '300px', flexShrink: 0, border: '1px solid ' + BorderColor2, borderRadius: '5px', padding: '14px', position: 'sticky', top: 0 }}>
					<FlexDiv justify='space-between' align='center'>
						<SectionTitle title={configField.name} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{configField.name}</SectionTitle>
						<span style={{ cursor: 'pointer', color: GreySidely3, padding: '0 4px' }} onClick={() => setConfigFieldId(undefined)}>✕</span>
					</FlexDiv>
					<label style={{ display: 'flex', gap: '6px', alignItems: 'center', cursor: 'pointer', margin: 0, fontSize: '12px' }}>
						<input type='checkbox' checked={links.has(configField.id)} onChange={() => toggleLink(configField)} />
						<Translate id='import.promotions.link_companies' />
					</label>
					{links.has(configField.id) && <Dropdown<number>
						cancellable
						dropdownStyle={creationPopupDropdownStyle}
						name=''
						placeholder={translateToString('additional_column')}
						datalist={additionalColumns.map(ac => ({ value: ac.id, label: ac.name }))}
						selectedValue={links.get(configField.id) !== undefined ? { value: links.get(configField.id)!, label: additionalColumns.find(ac => ac.id === links.get(configField.id))?.name ?? '' } : undefined}
						onChange={(v) => setLinks(prev => new Map(prev).set(configField.id, v.value ?? undefined))}
					/>}
					<p style={hintStyle}>
						<Translate id='import.promotions.p1' /><br />
						<Translate id='import.promotions.multi' /><br />
						<Translate id='import.promotions.p2' />
					</p>
				</FlexDiv>}
			</FlexDiv>;
		},
	});
	if (alertRes.res == AlertRes.Break) return;
	const { headers, pivotColumn, companyFieldMappings } = alertRes.data;
	const { campaigns, skipped } = transformData(headers, data, productBarcodeMap, productIdSet);
	if (campaigns.length === 0) return;
	return () => importPromotionsCall(campaigns, companyFieldMappings, pivotColumn).then(() => {
		if (skipped.length > 0) showSkippedProducts(alert, skipped);
	});
}

function downloadSkippedProductsCsv(skipped: SkippedProduct[]) {
	const quote = (value: string) => `"${value.replace(/"/g, '""')}"`;
	const rows = [
		[translateToString('import.promotions.skipped.line'), translateToString('import.sidely_fields.barcode'), translateToString('campagne')].map(quote).join(';'),
		...skipped.map(s => [String(s.line), quote(s.value), quote(s.name ?? '')].join(';')),
	];
	// the BOM keeps Excel from reading the accents as latin-1
	const blob = new Blob(['\ufeff' + rows.join('\n')], { type: 'text/csv;charset=utf-8' });
	const url = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = url;
	a.download = 'skipped_products.csv';
	a.click();
	URL.revokeObjectURL(url);
}

function showSkippedProducts(alert: AlertCall, skipped: SkippedProduct[]) {
	alert({
		title: translateToString('import.promotions.skipped.title'),
		width: 'min(560px, 95vw)',
		content: () => <FlexDiv flow='column' align='stretch' gap='10px'>
			<p style={{ margin: 0, fontSize: '13px' }}>{translateToString('import.promotions.skipped.message', [['count', String(skipped.length)]])}</p>
			<div style={{ maxHeight: '300px', overflowY: 'auto', fontSize: '12px' }}>
				{skipped.map((s, i) => <FlexDiv key={`skippedProduct[${i}]`} gap='10px'>
					<span style={{ color: GreySidely3, width: '70px', flexShrink: 0 }}>{translateToString('import.promotions.skipped.line')} {s.line}</span>
					<span style={{ fontWeight: 500 }}>{s.value}</span>
					{s.name && <span style={{ color: GreySidely3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>}
				</FlexDiv>)}
			</div>
			<FlexDiv justify='center'>
				<DefaultButton margin='5px 0 0 0' onClick={() => downloadSkippedProductsCsv(skipped)}><Translate id='import.promotions.skipped.download' /></DefaultButton>
			</FlexDiv>
		</FlexDiv>,
	});
}

export default function importPromotions(onImport: (t: (() => Promise<void>) | undefined) => void, products: Product[], alert: AlertCall, fields: Field[]) {
	const productBarcodeMap = new Map(products.filter(p => p.barcode).map(p => [p.barcode!, p.id]));
	const productIdSet = new Set(products.map(p => p.id));
	importWrapper<TemplateImport>(
		(data) => transformAndImportData(data, productBarcodeMap, productIdSet, alert).then(onImport),
		alert,
		() => {
			// both-ticked (promotion + product) fields are link-level and don't
			// import into promotions.fields. Headers use display names — the
			// mapping popup auto-fills by name, ids are gone from the template.
			const promoFields = fields.filter(f => f.is_promotion && !f.is_product);
			const productsWithBarcode = products.filter(p => p.barcode);
			const nameHeader = translateToString('name');
			const barcodeHeader = translateToString('import.sidely_fields.barcode');
			const periodHeader = (i: number, label: string) => `${translateToString('period')} ${i} - ${label}`;
			let productIndex = 0;
			function genRow(name: string) {
				let now = moment();
				const res: TemplateImport[] = [{
					[nameHeader]: name,
					[translateToString('description')]: 'My promotion description',
					[barcodeHeader]: productsWithBarcode[productIndex++]?.barcode ?? undefined,
					[periodHeader(1, translateToString('name'))]: `${translateToString('period')} 1`,
					[periodHeader(1, translateToString('color'))]: PrimaryColor,
					[periodHeader(1, translateToString('import.sidely_fields.start_date'))]: now.subtract(5, 'day').format('YYYY-MM-DD'),
					[periodHeader(1, translateToString('import.sidely_fields.end_date'))]: now.add(3, 'day').format('YYYY-MM-DD'),
					[periodHeader(2, translateToString('name'))]: `${translateToString('period')} 2`,
					[periodHeader(2, translateToString('color'))]: RedSidely,
					[periodHeader(2, translateToString('import.sidely_fields.start_date'))]: now.add(1, 'day').format('YYYY-MM-DD'),
					[periodHeader(2, translateToString('import.sidely_fields.end_date'))]: now.add(5, 'day').format('YYYY-MM-DD')
				}];
				for (let i = 0; i < 3; i++) {
					if (productIndex < productsWithBarcode.length) {
						res.push({ [nameHeader]: name, [barcodeHeader]: productsWithBarcode[productIndex++].barcode });
					}
				}
				if (productIndex >= productsWithBarcode.length) {
					productIndex = 0;
				}
				now = moment();
				res.forEach((row, i) => {
					promoFields.forEach(field => {
						switch (field.type) {
							case 'Text':
							case 'TextArea':
								row[field.name] = 'Test text field';
								break;
							case 'Integer':
								row[field.name] = Math.floor(Math.random() * 100);
								break;
							case 'Money':
								row[field.name] = Math.floor((Math.random() + 1) * 500);
								break;
							case 'Boolean':
								row[field.name] = true;
								break;
							case 'Number':
								row[field.name] = Math.random() * 100;
								break;
							case 'Date':
								row[field.name] = now.toISOString();
								break;
							case 'Select': {
								const data = field.data as string[] | undefined;
								if (i < (data?.length ?? 0)) row[field.name] = data?.[i];
								break;
							}
						}

					});

				});
				return res;
			}

			return ['my first promotion', 'my second promotion', 'my third promotion'].map(genRow).flat();
		});
}
